import { createStyles, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';

import { ArrowRightIcon } from 'src/components/icons';
import { BankVerifyFormModel } from 'src/constants/componentTypes/paymentsTypes';
import { useAppDispatch } from 'src/hooks/useStore';
import Button from 'src/legacy/components/Button';
import { FormModal } from 'src/legacy/components/Modals';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { alertSnackbar } from 'src/store/ui/actions';
import { ensureApiError } from 'src/utils/Errors';

import { useBankVerifyForm } from './useBankVerifyForm';

const useStyles = makeStyles(() =>
  createStyles({
    verifyButton: {
      textAlign: 'right',
    },
    disableInteraction: {
      pointerEvents: 'none',
    },
  }),
);

export interface BankVerifyActionProps {
  onVerify: (values: BankVerifyFormModel) => Promise<void>;
}

export interface BankAccountSource {
  id: string;
  bank_name?: string | null;
  last4?: string | null;
}

export interface BankVerifyButtonProps {
  source: BankAccountSource;
  previewMode?: boolean;
  className?: string;
}

export const BankVerifyButton: React.FC<
  BankVerifyActionProps & BankVerifyButtonProps
> = ({ source, onVerify, previewMode = false, className }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [showVerifyModal, setShowVerifyModal] = React.useState(false);
  const bankVerification: BankVerifyFormModel = React.useMemo(
    () => ({
      id: source.id,
      fromAmount: '',
      toAmount: '',
      bank_name: source.bank_name ?? '',
      last4: source.last4 ?? '',
    }),
    [source.id, source.bank_name, source.last4],
  );

  /**
   * verify bank micro-deposit amounts using prop function
   * and handle errors that may occur by showing snackbar
   */
  const handleVerifyBank = async (values: BankVerifyFormModel) => {
    let errorMsg;
    try {
      await onVerify(values);
    } catch (e) {
      const err = ensureApiError(e);
      console.info('Caught error', err);
      if (err.message) {
        errorMsg = err.message;
      } else {
        errorMsg = err.message;
      }
    }

    if (errorMsg) {
      dispatch(alertSnackbar({ errorMessage: errorMsg }));
    }
  };

  return (
    <>
      <Button
        htmlId={`${source.id}-verify`}
        variant="text"
        color="primary"
        endIcon={<ArrowRightIcon style={{ fontSize: 12 }} />}
        onClick={() => setShowVerifyModal(true)}
        containerClass={clsx(classes.verifyButton, className)}
        className={`${previewMode ? classes.disableInteraction : ''}`}
      >
        <BaseTypography fontType="13Medium">Verify</BaseTypography>
      </Button>
      <FormModal
        initialFormValue={bankVerification}
        title="Verify bank account"
        buttonLabel="Verify account"
        handleSave={handleVerifyBank}
        onClose={() => setShowVerifyModal(false)}
        useFormHook={useBankVerifyForm}
        open={showVerifyModal}
      />
    </>
  );
};
